import { FormGroup } from '@angular/forms';
import { addRemoveError, checkBoxHelper, getControl, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty, isNilOrEmptyOrZero } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { isEqual } from 'lodash-es';
import { REQUIRED_STATUS, THE_AMMOUNT_MUST_BE_ZERO } from '@shared/constants/shared.constants';
import { formPaths } from '@shared/constants/form-paths.const';

const SCHEDA_PREV_PATH = `${formPaths.naturaMutualistica}.my_SchedaPrevalenza`;

export const getNatMutGroup = (form: FormGroup): FormGroup =>
    getGroup(form, formPaths.naturaMutualistica);

export const getNatMutControl = (form: FormGroup, path: string) =>
    getControl(form, `${formPaths.naturaMutualistica}.${path}`);

export const getSchedaPrevalenzaGroup = (form: FormGroup): FormGroup =>
    getGroup(form, SCHEDA_PREV_PATH);

export const getSchedaPrevalenzaControl = (form: FormGroup, path: string) =>
    getControl(form, `${SCHEDA_PREV_PATH}.${path}`);

export const natMutCkHelper = (
    form: FormGroup,
    targetPath: string,
    ckPath: string,
    ckTargetValue: number,
    subFormPath: string = formPaths.naturaMutualistica,
    suppCondition = true,
    errorStatus?: string
): void => checkBoxHelper(form, targetPath, ckPath, ckTargetValue, subFormPath, suppCondition, errorStatus);

/** Valida un campo numerico della schedaPrevalenza rispetto al ck abilitante e al totale */
export const schedaPrevalenzaValuesHelper = (
    form: FormGroup,
    targetPath: string,
    my_CkPath: string,
    my_CkCoopAttPath: string,
    my_TotalePath: string,
    my_BilAnnoPath: string,
    fieldName: string,
    invalidMessage: string,
    forTotal = false,
    suppCondition = true
): void => {
    const naturaMutualisticaGroup = getNatMutGroup(form);
    const schedaPrevalenzaGroup = getSchedaPrevalenzaGroup(form);
    const targetControl = getControl(schedaPrevalenzaGroup, targetPath);
    const my_Ck = toNum(getControl(naturaMutualisticaGroup, my_CkPath)?.value);
    const my_CkCoopAtt = toNum(getControl(schedaPrevalenzaGroup, my_CkCoopAttPath)?.value);
    const my_Totale = !!my_TotalePath ? toNum(getControl(schedaPrevalenzaGroup, my_TotalePath)?.value) : null;
    const my_BilAnno: string = getControl(form, `${formPaths.datiBilancio}.${my_BilAnnoPath}`)?.value;
    const showRequiredError = isNilOrEmpty(targetControl?.value) && !isNilOrEmpty(my_BilAnno) && toNum(my_Ck) === 1;
    const showMustBeZeroError = my_Ck === 1 && toNum(targetControl?.value) !== 0 && !isNilOrEmpty(my_BilAnno) && my_CkCoopAtt === 0;
    const showInvalidError = my_Ck === 0 && !isNilOrEmptyOrZero(targetControl?.value);
    const showGreterThenTotale = forTotal ? false : !isNilOrEmpty(targetControl?.value) && toNum(targetControl?.value) > my_Totale;
    const errors = targetControl.errors ? targetControl.errors : {};

    if (!forTotal) {
        // Il controllo "maggiore del Totale" non è mai condizionato da suppCondition (V/Q) in InfoPath
        // (manifest.xsf: A1versoSoci/B9versoSoci/B7ricevSoci/B6confSoci/QtaConfSoci, incl. PrimoAnno,
        // hanno un'unica errorCondition ". > Totale" priva di qualunque gate) — a differenza di
        // required/invalid/mustBeZero, che invece SONO condizionati da suppCondition.
        addRemoveError(showGreterThenTotale, errors, targetPath, {
            status: `${fieldName} verso soci maggiore del Totale ${fieldName}`,
            message: `La voce ${fieldName} verso soci non può essere superiore alla voce Totale ${fieldName}`,
        });
    }

    addRemoveError(showInvalidError && suppCondition, errors, targetPath, {
        status: 'Non è possibile indicare un valore',
        message: invalidMessage,
    });

    addRemoveError(showMustBeZeroError && suppCondition, errors, targetPath, {
        status: THE_AMMOUNT_MUST_BE_ZERO,
    });

    addRemoveError(showRequiredError && suppCondition, errors, targetPath, {
        status: REQUIRED_STATUS,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};

/** Valida il campo commento della schedaPrevalenza rispetto a valore/totale e ck abilitante */
export const schedaPrevalenzaCommHelper = (
    form: FormGroup,
    targetPath: string,
    ckPath: string | undefined,
    valuePath: string,
    totValuePath: string,
    totValueNoModPath: string,
    suppCondition = true
): void => {
    const natMutGroup = getNatMutGroup(form);
    const schedaPrevalenzaGroup = getGroup(natMutGroup, 'my_SchedaPrevalenza');
    const targetControl = getControl(schedaPrevalenzaGroup, targetPath);
    const my_Ck: string | undefined = ckPath ? getControl(natMutGroup, ckPath)?.value : undefined;
    const baseValue = toNum(getControl(schedaPrevalenzaGroup, valuePath)?.value);
    const totValue = toNum(getControl(schedaPrevalenzaGroup, totValuePath)?.value);
    const toValueNoMod = totValueNoModPath ? toNum(getControl(schedaPrevalenzaGroup, totValueNoModPath)?.value) : null;
    const errors = targetControl.errors ? targetControl.errors : {};
    const totNoModCondition = totValueNoModPath ? toValueNoMod > 0 : true;
    const ckCondition = ckPath ? my_Ck === '1' : true;
    const showError = baseValue === 0 && totValue === 0 && ckCondition && totNoModCondition && isNilOrEmpty(targetControl?.value);

    addRemoveError(showError && suppCondition, errors, targetPath, {
        status: REQUIRED_STATUS,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};
